import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';

function scannedTables(tables) {
  return (tables || []).filter((t) => t && t.scanStatus !== 'removed');
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

function isValidTableName(name) {
  return /^[a-z][a-z0-9_]{0,62}$/.test(String(name || ''));
}

const ExcelTemplateImportDialog = ({
  open,
  onClose,
  catalog,
  getAuthHeaders,
  onImported,
}) => {
  const { showSuccess, showError } = useNotification();
  const tables = useMemo(() => scannedTables(catalog?.tables), [catalog]);
  const [file, setFile] = useState(null);
  const [fileBase64, setFileBase64] = useState('');
  const [preview, setPreview] = useState(null);
  const [tableName, setTableName] = useState('');
  const [parentTableId, setParentTableId] = useState('');
  const [joinColumn, setJoinColumn] = useState('');
  const [confirmAppend, setConfirmAppend] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setFileBase64('');
    setPreview(null);
    setTableName('');
    setParentTableId('');
    setJoinColumn('');
    setConfirmAppend(false);
  }, [open]);

  const parentTable = useMemo(
    () => tables.find((t) => t.id === parentTableId) || null,
    [tables, parentTableId]
  );

  const taken = useMemo(
    () => new Set((preview?.takenTableNames || []).map((n) => String(n).toLowerCase())),
    [preview]
  );
  const nameOk = isValidTableName(tableName);
  const nameTaken = nameOk && taken.has(tableName);
  const nameAvailable = nameOk && !nameTaken;
  const existingColumns = preview?.existingTableColumns?.[tableName] || null;
  const compatibility = useMemo(() => {
    if (!nameTaken || !preview) {
      return { compatible: false, missingInExcel: [], extraInExcel: [] };
    }
    const excelNames = (preview.columns || []).map((c) => c.name);
    const existing = existingColumns || [];
    const excelSet = new Set(excelNames);
    const existingSet = new Set(existing);
    return {
      compatible:
        existing.length > 0 &&
        existing.every((c) => excelSet.has(c)) &&
        excelNames.every((c) => existingSet.has(c)),
      missingInExcel: existing.filter((c) => !excelSet.has(c)),
      extraInExcel: excelNames.filter((c) => !existingSet.has(c)),
    };
  }, [nameTaken, preview, existingColumns]);

  const previewMutation = useMutation({
    mutationFn: async ({ base64, parentId, standalone }) => {
      const headers = getAuthHeaders?.() || {};
      return axios.post(
        '/api/admin/data-model/excel-import/preview',
        {
          connectionId: catalog.connectionId,
          database: catalog.database,
          fileBase64: base64,
          parentTableId: parentId || undefined,
          standalone: Boolean(standalone),
        },
        { headers }
      );
    },
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      const headers = getAuthHeaders?.() || {};
      return axios.post(
        '/api/admin/data-model/excel-import',
        {
          connectionId: catalog.connectionId,
          database: catalog.database,
          fileBase64,
          parentTableId: parentTableId || undefined,
          tableName,
          joinColumn: parentTableId ? joinColumn : undefined,
          confirmAppend,
          standalone: !parentTableId,
        },
        { headers }
      );
    },
  });

  const applyPreview = (data) => {
    setPreview(data);
    setTableName(data.suggestedTableName || '');
    setParentTableId(data.parentTableId || parentTableId || '');
    setJoinColumn(data.joinColumn || '');
  };

  const handleFile = async (nextFile) => {
    setFile(nextFile || null);
    setPreview(null);
    if (!nextFile) {
      setFileBase64('');
      return;
    }
    try {
      const base64 = await fileToBase64(nextFile);
      setFileBase64(base64);
      const res = await previewMutation.mutateAsync({ base64, parentId: parentTableId });
      if (res.data.success) applyPreview(res.data);
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al leer el Excel');
    }
  };

  const handleParentChange = async (nextId) => {
    setParentTableId(nextId || '');
    if (!fileBase64) return;
    try {
      const res = await previewMutation.mutateAsync({
        base64: fileBase64,
        parentId: nextId,
        standalone: !nextId,
      });
      if (res.data.success) {
        setPreview(res.data);
        setTableName((prev) => prev || res.data.suggestedTableName || '');
        setJoinColumn(res.data.joinColumn || '');
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al actualizar el preview');
    }
  };

  const handleImport = async () => {
    try {
      const res = await importMutation.mutateAsync();
      if (res.data.success) {
        showSuccess(
          res.data.appended
            ? `Se agregaron ${res.data.insertedCount || 0} filas nuevas a ${res.data.schema}.${res.data.tableName}` +
                (res.data.skippedCount ? ` (${res.data.skippedCount} ya existían)` : '')
            : `Tabla ${res.data.schema}.${res.data.tableName} creada (${res.data.rowCount} filas)`
        );
        onImported?.(res.data.catalog);
        onClose?.();
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al importar');
    }
  };

  const busy = previewMutation.isPending || importMutation.isPending;
  const canImport = Boolean(
    fileBase64 &&
      preview &&
      (!parentTableId || joinColumn) &&
      !busy &&
      (nameAvailable || (nameTaken && compatibility.compatible && confirmAppend))
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Importar plantilla</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Sube el Excel exportado (puedes haber agregado columnas). Se creará una tabla en el schema{' '}
          <strong>external_data</strong>. La tabla padre es opcional: si no eliges una, queda como
          tabla independiente.
        </Typography>
        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
          {file ? file.name : 'Elegir archivo .xlsx'}
          <input
            hidden
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => handleFile(e.target.files?.[0] || null)}
          />
        </Button>
        {previewMutation.isPending && !preview && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Leyendo Excel…</Typography>
          </Box>
        )}
        {preview && (
          <>
            <TextField
              size="small"
              label="Nombre de la tabla"
              value={tableName}
              onChange={(e) => {
                setTableName(e.target.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'));
                setConfirmAppend(false);
              }}
              disabled={busy}
              helperText={
                !tableName
                  ? `Sugerido: ${preview.suggestedTableName}`
                  : !nameOk
                    ? 'Usa minúsculas, números y _ ; debe empezar con letra'
                    : nameTaken
                      ? `Ya existe external_data.${tableName}. Si las columnas coinciden puedes agregar filas nuevas.`
                      : `Se creará external_data.${tableName}`
              }
              error={Boolean(tableName) && !nameOk}
            />
            {nameTaken && (
              <>
                {!compatibility.compatible ? (
                  <Alert severity="error">
                    Las columnas no coinciden con la tabla existente
                    {compatibility.missingInExcel.length
                      ? `. Faltan en el Excel: ${compatibility.missingInExcel.join(', ')}`
                      : ''}
                    {compatibility.extraInExcel.length
                      ? `. Extra en el Excel: ${compatibility.extraInExcel.join(', ')}`
                      : ''}
                    .
                  </Alert>
                ) : (
                  <Alert severity="info">
                    La tabla ya existe y las columnas coinciden. Solo se insertarán filas nuevas
                    (claves que aún no estén cargadas). Las filas existentes no se modifican.
                  </Alert>
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmAppend}
                      onChange={(e) => setConfirmAppend(e.target.checked)}
                      disabled={busy || !compatibility.compatible}
                    />
                  }
                  label={`Estoy seguro de agregar datos en la tabla existente external_data.${tableName}`}
                />
              </>
            )}
            <Autocomplete
              size="small"
              options={tables}
              value={parentTable}
              onChange={(_, option) => handleParentChange(option?.id || '')}
              getOptionLabel={(t) => t?.id || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              disabled={busy}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tabla padre (opcional)"
                  placeholder="Vacío = tabla independiente"
                  helperText="Si la dejas vacía, no se crea relación ni FK."
                />
              )}
            />
            {parentTableId && (
              <FormControl size="small" fullWidth disabled={busy || !preview.columns?.length}>
                <InputLabel>Columna de relación</InputLabel>
                <Select
                  label="Columna de relación"
                  value={joinColumn}
                  onChange={(e) => setJoinColumn(e.target.value)}
                >
                  {(preview.columns || []).map((c) => (
                    <MenuItem key={c.name} value={c.name}>
                      {c.name}
                      {c.inParent ? ' (en la padre)' : ''}
                      {` · ${c.inferredType}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {!parentTableId && (
              <Alert severity="info">
                Se importará como tabla independiente, sin relación con otras tablas del modelo.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {preview.rowCount} filas · {preview.columns?.length || 0} columnas
              {preview.duplicateKeyCount
                ? ` · ${preview.duplicateKeyCount} claves repetidas en ${joinColumn || 'la relación'}`
                : ''}
            </Typography>
            {parentTableId && preview.orphanCount > 0 && (
              <Alert severity="warning">
                {preview.orphanCount} valor(es) de {joinColumn} no están en la tabla padre
                {preview.orphanSample?.length
                  ? ` (ej. ${preview.orphanSample.join(', ')})`
                  : ''}
                . Se creará la tabla, pero sin FK en la base.
              </Alert>
            )}
            {parentTableId && preview.canCreateForeignKey && (
              <Alert severity="info">
                Se creará una FK real hacia {parentTableId}.{joinColumn}.
              </Alert>
            )}
            <TableContainer sx={{ maxHeight: 220, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {(preview.columns || []).map((c) => (
                      <TableCell key={c.name}>{c.name}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(preview.sampleRows || []).map((row, i) => (
                    <TableRow key={i}>
                      {(preview.columns || []).map((c) => (
                        <TableCell key={c.name}>
                          {row[c.name] == null ? '' : String(row[c.name])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!canImport}
          startIcon={importMutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {nameTaken ? 'Agregar filas nuevas' : 'Crear tabla'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExcelTemplateImportDialog;
